import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, RefreshCw,
  Filter, Car, Home, Package, ShoppingCart, Wrench, Shirt,
  Clock, User, MapPin, DollarSign, Check, X, AlertCircle,
  Eye, ChevronDown, UserPlus, Zap, Users
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';
import ShiftCalendarView from './ShiftCalendarView';

// Service type configurations - 4 Core Services (Rides PRIMARY)
const SERVICE_CONFIG = {
  ride: { icon: Car, color: '#8B5CF6', label: 'Rides', bg: 'bg-purple-100' },
  rides: { icon: Car, color: '#8B5CF6', label: 'Rides', bg: 'bg-purple-100' },
  laundry: { icon: Shirt, color: '#06B6D4', label: 'Laundry', bg: 'bg-cyan-100' },
  courier: { icon: Package, color: '#F59E0B', label: 'Courier', bg: 'bg-amber-100' },
  errands: { icon: ShoppingCart, color: '#EC4899', label: 'Errands', bg: 'bg-pink-100' },
};

const STATUS_CONFIG = {
  requested: { color: 'bg-blue-500', label: 'Requested' },
  pending: { color: 'bg-yellow-500', label: 'Pending' },
  pending_driver: { color: 'bg-yellow-500', label: 'Finding Driver' },
  confirmed: { color: 'bg-indigo-500', label: 'Confirmed' },
  assigned: { color: 'bg-purple-500', label: 'Assigned' },
  driver_assigned: { color: 'bg-purple-500', label: 'Driver Assigned' },
  en_route_pickup: { color: 'bg-orange-500', label: 'En Route' },
  in_progress: { color: 'bg-orange-500', label: 'In Progress' },
  completed: { color: 'bg-green-500', label: 'Completed' },
  cancelled: { color: 'bg-red-500', label: 'Cancelled' },
};

const MasterScheduleTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('day'); // day, week, month, shifts
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Quick Assign state
  const [showQuickAssign, setShowQuickAssign] = useState(false);
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    serviceTypes: [],
    workerId: '',
    status: [],
    paymentStatus: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [workers, setWorkers] = useState([]);

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    
    if (viewMode === 'day') {
      // Just the current day
    } else if (viewMode === 'week') {
      // Start of week (Sunday)
      start.setDate(start.getDate() - start.getDay());
      end.setDate(start.getDate() + 6);
    } else if (viewMode === 'month') {
      // First and last day of month
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, [currentDate, viewMode]);

  useEffect(() => {
    fetchEvents();
    fetchWorkers();
  }, [dateRange, filters]);

  useEffect(() => {
    if (viewMode === 'day') {
      fetchSummary();
    }
  }, [currentDate, viewMode]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (filters.serviceTypes.length > 0) {
        params.append('service_types', filters.serviceTypes.join(','));
      }
      if (filters.workerId) {
        params.append('worker_id', filters.workerId);
      }
      if (filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      if (filters.paymentStatus) {
        params.append('payment_status', filters.paymentStatus);
      }
      
      const response = await api.get(`/schedule/events?${params}`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch schedule events:', error);
      toast.error('Failed to load schedule');
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const dateStr = currentDate.toISOString().split('T')[0];
      const response = await api.get(`/schedule/summary?date=${dateStr}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchWorkers = async () => {
    try {
      const dateStr = currentDate.toISOString().split('T')[0];
      const response = await api.get(`/schedule/workers/availability?date=${dateStr}`);
      setWorkers(response.data.workers || []);
    } catch (error) {
      console.error('Failed to fetch workers:', error);
    }
  };

  // Fetch available drivers for Quick Assign
  const fetchAvailableDrivers = async (event) => {
    try {
      const dateStr = currentDate.toISOString().split('T')[0];
      const response = await api.get(`/schedule/workers/availability?date=${dateStr}`);
      
      // Filter for drivers if it's a ride, otherwise show all workers
      let availableWorkers = response.data.workers || [];
      if (event.type === 'ride') {
        availableWorkers = availableWorkers.filter(w => w.is_driver && w.is_available);
      } else {
        availableWorkers = availableWorkers.filter(w => w.is_available);
      }
      
      setAvailableDrivers(availableWorkers);
    } catch (error) {
      console.error('Failed to fetch available drivers:', error);
      toast.error('Failed to load available workers');
    }
  };

  // Quick Assign handler
  const handleQuickAssign = async (driverId) => {
    if (!selectedEvent) return;
    
    setAssigningDriver(true);
    try {
      const eventType = selectedEvent.type === 'ride' ? 'ride' : 'booking';
      
      const response = await api.post(`/schedule/quick-assign?event_type=${eventType}&event_id=${selectedEvent.id}&driver_id=${driverId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowQuickAssign(false);
        setSelectedEvent(null);
        fetchEvents(); // Refresh the calendar
      } else {
        // Show conflict warning
        toast.error(response.data.message);
        if (response.data.conflicts?.length > 0) {
          const conflictMsg = response.data.conflicts.map(c => 
            `${c.type}: ${c.customer_name} at ${new Date(c.start_time).toLocaleTimeString()}`
          ).join(', ');
          toast.warning(`Conflicts: ${conflictMsg}`);
        }
      }
    } catch (error) {
      console.error('Quick assign error:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
    setAssigningDriver(false);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Group events by hour for day view
  const eventsByHour = useMemo(() => {
    if (viewMode !== 'day') return {};
    
    const grouped = {};
    const dateStr = currentDate.toISOString().split('T')[0];
    
    events.forEach(event => {
      if (!event.start_time?.startsWith(dateStr)) return;
      
      const hour = new Date(event.start_time).getHours();
      if (!grouped[hour]) grouped[hour] = [];
      grouped[hour].push(event);
    });
    
    return grouped;
  }, [events, viewMode, currentDate]);

  // Group events by date for week/month view
  const eventsByDate = useMemo(() => {
    if (viewMode === 'day') return {};
    
    const grouped = {};
    events.forEach(event => {
      const date = event.start_time?.split('T')[0];
      if (!date) return;
      
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(event);
    });
    
    return grouped;
  }, [events, viewMode]);

  // Generate week days for week view
  const weekDays = useMemo(() => {
    const days = [];
    const start = new Date(dateRange.start);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    
    return days;
  }, [dateRange]);

  // Generate month days for month view
  const monthDays = useMemo(() => {
    const days = [];
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const startDay = start.getDay();
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    
    // Previous month padding
    for (let i = 0; i < startDay; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() - (startDay - i));
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ 
        date: new Date(currentDate.getFullYear(), currentDate.getMonth(), i), 
        isCurrentMonth: true 
      });
    }
    
    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  }, [currentDate]);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const toggleServiceFilter = (service) => {
    setFilters(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(service)
        ? prev.serviceTypes.filter(s => s !== service)
        : [...prev.serviceTypes, service]
    }));
  };

  const toggleStatusFilter = (status) => {
    setFilters(prev => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status]
    }));
  };

  const renderEventCard = (event, compact = false) => {
    const config = SERVICE_CONFIG[event.type] || { icon: CalendarIcon, color: '#6B7280', bg: 'bg-gray-100' };
    const Icon = config.icon;
    const statusConfig = STATUS_CONFIG[event.status] || { color: 'bg-gray-500', label: event.status };

    if (compact) {
      return (
        <div
          key={event.id}
          className={`p-1.5 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity ${config.bg}`}
          style={{ borderLeft: `3px solid ${config.color}` }}
          onClick={() => setSelectedEvent(event)}
          data-testid={`event-${event.id}`}
        >
          <div className="flex items-center gap-1 truncate">
            <Icon className="w-3 h-3 flex-shrink-0" style={{ color: config.color }} />
            <span className="truncate">{event.title}</span>
          </div>
        </div>
      );
    }

    return (
      <motion.div
        key={event.id}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-3 rounded-lg cursor-pointer hover:shadow-md transition-shadow ${config.bg}`}
        style={{ borderLeft: `4px solid ${config.color}` }}
        onClick={() => setSelectedEvent(event)}
        data-testid={`event-${event.id}`}
      >
        <div className="flex items-start gap-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: config.color }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium truncate">{event.title}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] text-white ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
            </div>
            
            <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(event.start_time)}
              </span>
              {event.duration_minutes && (
                <span>{event.duration_minutes} min</span>
              )}
              {event.worker_name && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {event.worker_name}
                </span>
              )}
            </div>
            
            {event.notes && (
              <p className="text-xs text-[var(--text-muted)] mt-1 truncate">{event.notes}</p>
            )}
          </div>
          
          {event.amount && (
            <div className="text-right">
              <span className="font-semibold text-[var(--primary)]">${event.amount.toFixed(2)}</span>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-4" data-testid="master-schedule-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-[var(--primary)]" />
            Schedule
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Unified calendar for all services
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            {['day', 'week', 'month'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                  viewMode === mode 
                    ? 'bg-[var(--primary)] text-white' 
                    : 'bg-[var(--background-subtle)] hover:bg-[var(--background-paper)] text-[var(--text-secondary)]'
                }`}
              >
                {mode}
              </button>
            ))}
            <button
              onClick={() => setViewMode('shifts')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-1 ${
                viewMode === 'shifts' 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--background-subtle)] hover:bg-[var(--background-paper)] text-[var(--text-secondary)]'
              }`}
              data-testid="view-shifts-btn"
            >
              <Users className="w-3.5 h-3.5" />
              Shifts
            </button>
          </div>
          
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {(filters.serviceTypes.length > 0 || filters.status.length > 0) && (
              <span className="ml-1 w-5 h-5 bg-[var(--primary)] text-white rounded-full text-xs flex items-center justify-center">
                {filters.serviceTypes.length + filters.status.length}
              </span>
            )}
          </Button>
          
          <Button variant="outline" size="sm" onClick={fetchEvents}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="card p-4 space-y-4"
        >
          {/* Service Type Filters */}
          <div>
            <label className="text-sm font-medium mb-2 block">Service Types</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(SERVICE_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const isActive = filters.serviceTypes.includes(key);
                
                return (
                  <button
                    key={key}
                    onClick={() => toggleServiceFilter(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                      isActive 
                        ? 'text-white border-transparent' 
                        : 'bg-[var(--background-subtle)] border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--background-paper)]'
                    }`}
                    style={isActive ? { backgroundColor: config.color } : {}}
                    data-testid={`filter-${key}`}
                  >
                    <Icon className="w-4 h-4" />
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Status Filters */}
          <div>
            <label className="text-sm font-medium mb-2 block">Status</label>
            <div className="flex flex-wrap gap-2">
              {['pending', 'assigned', 'in_progress', 'completed', 'cancelled'].map(status => {
                const isActive = filters.status.includes(status);
                const config = STATUS_CONFIG[status] || { color: 'bg-gray-500', label: status };
                
                return (
                  <button
                    key={status}
                    onClick={() => toggleStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                      isActive 
                        ? `${config.color} text-white border-transparent` 
                        : 'bg-[var(--background-subtle)] border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--background-paper)]'
                    }`}
                    data-testid={`filter-status-${status}`}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Worker Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">Worker/Driver</label>
            <select
              value={filters.workerId}
              onChange={(e) => setFilters(prev => ({ ...prev, workerId: e.target.value }))}
              className="w-full md:w-64 px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">All Workers</option>
              {workers.map(worker => (
                <option key={worker.id} value={worker.id}>
                  {worker.name} {worker.is_driver ? '(Driver)' : ''}
                </option>
              ))}
            </select>
          </div>
          
          {/* Clear Filters */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setFilters({ serviceTypes: [], workerId: '', status: [], paymentStatus: '' })}
          >
            Clear All Filters
          </Button>
        </motion.div>
      )}

      {/* Date Navigation - Hide for shifts view (has its own) */}
      {viewMode !== 'shifts' && (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigateDate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigateDate(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <h3 className="text-lg font-semibold">
          {viewMode === 'day' && currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          {viewMode === 'week' && `${formatDate(weekDays[0])} - ${formatDate(weekDays[6])}`}
          {viewMode === 'month' && currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
        
        {/* Summary for Day View */}
        {viewMode === 'day' && summary && (
          <div className="flex items-center gap-4 text-sm">
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
              {summary.totals?.total || 0} Events
            </span>
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
              {summary.by_status?.completed || 0} Completed
            </span>
          </div>
        )}
      </div>
      )}

      {/* Loading State - Hide for shifts view (has its own) */}
      {viewMode !== 'shifts' && loading ? (
        <div className="card p-12 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[var(--text-muted)]">Loading schedule...</p>
        </div>
      ) : (
        <>
          {/* DAY VIEW */}
          {viewMode === 'day' && (
            <div className="card overflow-hidden">
              {/* Time slots */}
              <div className="divide-y">
                {Array.from({ length: 14 }, (_, i) => i + 7).map(hour => {
                  const hourEvents = eventsByHour[hour] || [];
                  const timeStr = `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`;
                  
                  return (
                    <div key={hour} className="flex min-h-[80px]">
                      <div className="w-20 py-2 px-3 bg-[var(--background-subtle)] border-r text-sm text-[var(--text-muted)] font-medium">
                        {timeStr}
                      </div>
                      <div className="flex-1 p-2 space-y-2">
                        {hourEvents.length === 0 ? (
                          <div className="h-full flex items-center justify-center text-sm text-gray-300">
                            —
                          </div>
                        ) : (
                          hourEvents.map(event => renderEventCard(event))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* WEEK VIEW */}
          {viewMode === 'week' && (
            <div className="card overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Week Header */}
                <div className="grid grid-cols-7 border-b">
                  {weekDays.map(day => {
                    const dateStr = day.toISOString().split('T')[0];
                    const dayEvents = eventsByDate[dateStr] || [];
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    
                    return (
                      <div 
                        key={dateStr} 
                        className={`p-3 text-center border-r last:border-r-0 ${isToday ? 'bg-[var(--primary)]/5' : ''}`}
                      >
                        <div className="text-xs text-[var(--text-muted)]">
                          {day.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className={`text-lg font-semibold ${isToday ? 'text-[var(--primary)]' : ''}`}>
                          {day.getDate()}
                        </div>
                        <div className="text-xs text-[var(--text-muted)]">
                          {dayEvents.length} events
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Week Body */}
                <div className="grid grid-cols-7 min-h-[400px]">
                  {weekDays.map(day => {
                    const dateStr = day.toISOString().split('T')[0];
                    const dayEvents = eventsByDate[dateStr] || [];
                    
                    return (
                      <div key={dateStr} className="border-r last:border-r-0 p-2 space-y-1">
                        {dayEvents.slice(0, 5).map(event => renderEventCard(event, true))}
                        {dayEvents.length > 5 && (
                          <div className="text-xs text-center text-[var(--text-muted)] py-1">
                            +{dayEvents.length - 5} more
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* MONTH VIEW */}
          {viewMode === 'month' && (
            <div className="card overflow-hidden">
              {/* Month Header */}
              <div className="grid grid-cols-7 border-b bg-[var(--background-subtle)]">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-[var(--text-muted)]">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Month Grid */}
              <div className="grid grid-cols-7">
                {monthDays.map(({ date, isCurrentMonth }, idx) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const dayEvents = eventsByDate[dateStr] || [];
                  const isToday = dateStr === new Date().toISOString().split('T')[0];
                  
                  return (
                    <div 
                      key={idx} 
                      className={`min-h-[100px] p-1 border-b border-r ${
                        !isCurrentMonth ? 'bg-gray-50' : ''
                      } ${isToday ? 'bg-[var(--primary)]/5' : ''}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${
                        isCurrentMonth ? '' : 'text-gray-400'
                      } ${isToday ? 'text-[var(--primary)]' : ''}`}>
                        {date.getDate()}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map(event => renderEventCard(event, true))}
                        {dayEvents.length > 3 && (
                          <div className="text-[10px] text-center text-[var(--text-muted)]">
                            +{dayEvents.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* SHIFTS VIEW */}
      {viewMode === 'shifts' && (
        <ShiftCalendarView />
      )}

      {/* Empty State - Only show for non-shifts views */}
      {!loading && viewMode !== 'shifts' && events.length === 0 && (
        <div className="card p-12 text-center">
          <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
          <h3 className="text-lg font-semibold mb-2">No Events Scheduled</h3>
          <p className="text-[var(--text-muted)]">
            No {filters.serviceTypes.length > 0 ? filters.serviceTypes.join(', ') : 'services'} scheduled for this period
          </p>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto border border-[var(--border-primary)]"
          >
            <div className="p-4 border-b border-[var(--border-primary)] flex items-center justify-between sticky top-0 bg-[var(--background-paper)]">
              <h3 className="font-semibold">{selectedEvent.title}</h3>
              <Button variant="outline" size="sm" onClick={() => setSelectedEvent(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-sm text-white ${STATUS_CONFIG[selectedEvent.status]?.color || 'bg-gray-500'}`}>
                  {STATUS_CONFIG[selectedEvent.status]?.label || selectedEvent.status}
                </span>
                <span className="text-sm text-[var(--text-muted)] capitalize">
                  {selectedEvent.type} Service
                </span>
              </div>
              
              {/* Time */}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[var(--text-muted)]" />
                <span>{formatTime(selectedEvent.start_time)}</span>
                {selectedEvent.duration_minutes && (
                  <span className="text-[var(--text-muted)]">• {selectedEvent.duration_minutes} min</span>
                )}
              </div>
              
              {/* Customer */}
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[var(--text-muted)]" />
                <span>{selectedEvent.customer_name}</span>
                {selectedEvent.customer_phone && (
                  <a href={`tel:${selectedEvent.customer_phone}`} className="text-[var(--primary)] text-sm">
                    {selectedEvent.customer_phone}
                  </a>
                )}
              </div>
              
              {/* Worker */}
              {selectedEvent.worker_name && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[var(--text-muted)]" />
                  <span>Assigned to: <strong>{selectedEvent.worker_name}</strong></span>
                </div>
              )}
              
              {/* Location */}
              {selectedEvent.location && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[var(--text-muted)] mt-0.5" />
                  <div>
                    {selectedEvent.type === 'ride' ? (
                      <>
                        <p><span className="text-[var(--text-muted)]">From:</span> {selectedEvent.location.pickup?.address}, {selectedEvent.location.pickup?.city}</p>
                        <p><span className="text-[var(--text-muted)]">To:</span> {selectedEvent.location.dropoff?.address}, {selectedEvent.location.dropoff?.city}</p>
                      </>
                    ) : (
                      <p>{selectedEvent.location.address}, {selectedEvent.location.city} {selectedEvent.location.zip}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Amount */}
              {selectedEvent.amount && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[var(--text-muted)]" />
                  <span className="text-lg font-semibold text-[var(--primary)]">${selectedEvent.amount.toFixed(2)}</span>
                  {selectedEvent.payment_status && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      selectedEvent.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {selectedEvent.payment_status}
                    </span>
                  )}
                </div>
              )}
              
              {/* Notes */}
              {selectedEvent.notes && (
                <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-sm">
                  <p className="text-[var(--text-muted)]">{selectedEvent.notes}</p>
                </div>
              )}
              
              {/* Quick Assign Section */}
              {!selectedEvent.worker_id && ['requested', 'pending', 'pending_driver', 'confirmed'].includes(selectedEvent.status) && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      Quick Assign
                    </h4>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowQuickAssign(!showQuickAssign);
                        if (!showQuickAssign) fetchAvailableDrivers(selectedEvent);
                      }}
                    >
                      {showQuickAssign ? 'Hide' : 'Show'} {selectedEvent.type === 'ride' ? 'Drivers' : 'Workers'}
                    </Button>
                  </div>
                  
                  {showQuickAssign && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {availableDrivers.length === 0 ? (
                        <p className="text-sm text-[var(--text-muted)] text-center py-4">
                          No available {selectedEvent.type === 'ride' ? 'drivers' : 'workers'} found
                        </p>
                      ) : (
                        availableDrivers.map(driver => (
                          <div 
                            key={driver.id}
                            className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg hover:bg-[var(--background-accent)] transition-colors"
                          >
                            <div>
                              <p className="font-medium text-sm">{driver.name}</p>
                              <p className="text-xs text-[var(--text-muted)]">
                                {driver.scheduled_jobs} jobs • {driver.available_hours}h available
                              </p>
                            </div>
                            <Button 
                              size="sm" 
                              disabled={assigningDriver}
                              onClick={() => handleQuickAssign(driver.id)}
                              data-testid={`quick-assign-btn-${driver.id}`}
                            >
                              {assigningDriver ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <UserPlus className="w-4 h-4 mr-1" />
                                  Assign
                                </>
                              )}
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button className="flex-1" onClick={() => {
                  // Navigate to booking/ride detail
                  toast.info(`Opening ${selectedEvent.type} details...`);
                  setSelectedEvent(null);
                }}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Full Details
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MasterScheduleTab;
